import React, { useState, useRef, useEffect } from "react";
import { Button, Input, message } from "antd";
import { ArrowLeftOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import GenericModal from "../../../../Components/Modals/GenericModal";
import {
  getQRCodeById,
  revealQRCardNumber,
  updateFuelCardStatus,
} from "../../../../API/Admin/QRManagement";
import "./style.css";
import usePageAccess from "../../../../Utlis/usePageAccess";
import VehicleQRCode, {
  downloadVehicleQR,
} from "../../../../Components/VehicleQRCode";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const formatCurrency = (value) =>
  `$${(parseFloat(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatExpiry = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getMonth() + 1).padStart(2, "0")} / ${String(d.getFullYear()).slice(-2)}`;
};

const QRActiveDetail = () => {
  const { canEdit } = usePageAccess("qr-management");
  const navigate = useNavigate();
  const location = useLocation();
  const passedId = location.state?.qr?.key;

  const [qr, setQr] = useState(null);
  const [revealedCardNumber, setRevealedCardNumber] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [cardStatus, setCardStatus] = useState(null);
  const [freezeModalOpen, setFreezeModalOpen] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    if (!passedId) return;

    const fetchQR = async () => {
      try {
        const res = await getQRCodeById(passedId);

        if (Math.floor(res?.status / 100) === 2) {
          setQr(res?.data?.data || null);
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load QR code."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load QR code."),
        );
      }
    };

    fetchQR();
  }, [passedId]);

  const handleToggleCardNumber = async () => {
    if (showCardNumber) {
      setShowCardNumber(false);
      return;
    }

    if (revealedCardNumber) {
      setShowCardNumber(true);
      return;
    }

    if (!passedId) return;

    setRevealing(true);
    try {
      const res = await revealQRCardNumber(passedId);

      if (Math.floor(res?.status / 100) === 2) {
        setRevealedCardNumber(res?.data?.data?.card_number || null);
        setShowCardNumber(true);
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to reveal card number."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to reveal card number."),
      );
    } finally {
      setRevealing(false);
    }
  };

  const handleChangeCardStatus = async (status, reason) => {
    if (!passedId) return;

    setChangingStatus(true);
    try {
      const res = await updateFuelCardStatus(passedId, status, reason);

      if (Math.floor(res?.status / 100) === 2) {
        const updatedStatus = res?.data?.data?.status || status;
        setCardStatus(updatedStatus);
        setFrozen(updatedStatus === "frozen");
        setFreezeModalOpen(false);
        message.success(res?.data?.message || "Card status updated.");
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to update card status."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update card status."),
      );
    } finally {
      setChangingStatus(false);
    }
  };

  const statusLabel = cardStatus
    ? cardStatus.charAt(0).toUpperCase() + cardStatus.slice(1)
    : qr?.status || "-";
  const isStatusLocked = cardStatus
    ? cardStatus === "frozen" || cardStatus === "revoked"
    : qr?.status !== "Active";

  const handleDownloadQR = () => {
    downloadVehicleQR(qrRef.current, {
      licensePlate: qr?.license_plate,
      fileName: qr?.license_plate || qr?.qr_code,
    });
  };

  const formatMaskedForDisplay = (masked) =>
    masked ? masked.replace(/\*/g, "*").split("").join(" ") : "";

  return (
    <div className="qr-active-detail">
      <div className="qr-active-detail__back">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
      </div>

      <div className="qr-active-detail__header">
        <div className="qr-active-detail__header-left">
          <span className="qr-active-detail__title">Cab Sticker - {qr?.qr_code || "-"}</span>
          <span className={`qr-active-detail__status-pill ${isStatusLocked ? "locked" : "active"}`}>
            {statusLabel}
          </span>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          className="qr-active-detail__download-btn"
          onClick={handleDownloadQR}
          disabled={!qr?.qr_code}
        >
          DOWNLOAD QR
        </Button>
      </div>

      <div className="qr-active-detail__body">
        <div className="qr-active-detail__qr-card" ref={qrRef}>
          <div className="qr-active-detail__qr-label">Your Active QR Code</div>
          <div className="qr-active-detail__qr-code">
            <VehicleQRCode
              code={qr?.qr_code}
              licensePlate={qr?.license_plate}
            />
          </div>
          <div className="qr-active-detail__qr-sublabel">Scan at any Precision Terminal</div>
        </div>

        <div className="qr-active-detail__controls">
          <div className="qr-active-detail__controls-title">Credit Controls</div>
          <div className="qr-active-detail__field">
            <label className="qr-active-detail__label">Vehicle</label>
            <Input disabled value={qr?.vehicle_name || "-"} className="qr-active-detail__input" />
          </div>
          <div className="qr-active-detail__field-row">
            <div className="qr-active-detail__field">
              <label className="qr-active-detail__label">Fleet Group <span className="qr-active-detail__required">*</span></label>
              <Input disabled defaultValue="West Coast Division" className="qr-active-detail__input" />
            </div>
            <div className="qr-active-detail__field">
              <label className="qr-active-detail__label">Fuel Card Number <span className="qr-active-detail__required">*</span></label>
              <Input disabled value={qr?.card_number_masked || "-"} className="qr-active-detail__input" />
            </div>
          </div>
        </div>
      </div>

      <div className="qr-active-detail__card-section">
        <div className="qr-active-detail__fuel-card">
          <div className="qr-active-detail__card-top">
            <div>
              <div className="qr-active-detail__card-type">FLEET FUEL CARD</div>
              <div className="qr-active-detail__card-name">PRECISION SENTINEL</div>
            </div>
            <div className="qr-active-detail__card-nfc">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="rgba(255,255,255,0.4)" />
                <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" fill="rgba(255,255,255,0.6)" />
              </svg>
            </div>
          </div>
          <div className="qr-active-detail__card-number">
            {showCardNumber && revealedCardNumber
              ? formatMaskedForDisplay(revealedCardNumber)
              : qr?.card_number_masked || "-"}
          </div>
          <div className="qr-active-detail__card-footer">
            <div>
              <div className="qr-active-detail__card-footer-label">BALANCE</div>
              <div className="qr-active-detail__card-footer-value">
                {formatCurrency(qr?.balance)}
              </div>
            </div>
            <div>
              <div className="qr-active-detail__card-footer-label">EXPIRY</div>
              <div className="qr-active-detail__card-footer-value">
                {formatExpiry(qr?.expiry_date)}
              </div>
            </div>
            <div className="qr-active-detail__fleet-badge">FLT-9982</div>
          </div>
        </div>

        <div className="qr-active-detail__card-actions">
          <button
            className="qr-active-detail__card-btn"
            onClick={handleToggleCardNumber}
            disabled={revealing}
          >
            <EyeOutlined />
            {showCardNumber ? "Hide Card Number" : "Show Card Number"}
          </button>
          {canEdit && (
            <button
              className={`qr-active-detail__card-btn ${frozen ? "frozen" : ""}`}
              disabled={changingStatus}
              onClick={() =>
                frozen
                  ? handleChangeCardStatus("unassigned")
                  : setFreezeModalOpen(true)
              }
            >
              <span className="qr-active-detail__freeze-icon">❄</span>
              {frozen ? "Unfreeze Card" : "Freeze Card"}
            </button>
          )}
        </div>
      </div>

      <GenericModal
        open={freezeModalOpen}
        title="Freeze Fuel Card"
        subtitle="This will prevent any transactions on this card."
        showReason={true}
        textareaLabel="Reason for Freezing"
        textareaPlaceholder="Enter reason for freezing this card..."
        confirmText="Confirm Freeze"
        cancelText="Go Back"
        onCancel={() => setFreezeModalOpen(false)}
        onConfirm={(reason) => handleChangeCardStatus("frozen", reason)}
      />
    </div>
  );
};

export default QRActiveDetail;
